import React, {FC} from 'react';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import BarChartIcon from '@mui/icons-material/BarChart';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import JoinFullIcon from '@mui/icons-material/JoinFull';

import Panel from '@/components/Panels/common/Panel';
import InfoItem from '@/components/Panels/common/InfoItem';
import InfoBox from '@/components/Panels/common/InfoBox';

import {useTranslation} from 'react-i18next';

import {Stats} from '@/domain/entities/Stats';

import {PANEL_WIDTH} from '@/config';
import {formatNumber} from '@/utils';

//TYPES
export type Props = {
  stats?: Stats
};

const ZoneDataPanel: FC<Props> = ({stats}) => {

  const {t} = useTranslation();
  if(!stats) return null;

  const {
    owners, parcels, parcels_assigned, mean_parcels_by_owner,
    parcels_to_concentrate,
    area_to_concentrate, mean_area_by_owner, mean_area_by_parcel,
    min_area, max_area
  } = stats;

  return <Panel width={PANEL_WIDTH.zoneData}>
    <Stack direction="column">
      <Stack direction="row" sx={{alignItems: 'center', gap: 1}}>
        <BarChartIcon sx={{fontSize: '28px', color: 'primary.main'}}/>
        <Typography variant="h6" sx={{color: 'primary.main', m: 0, p: 0}}>{t('zoneData.title')}</Typography>
      </Stack>
      <Typography variant="body2" sx={{color: 'text.secondary', pl: 0.5, mb: 1}}>{t('zoneData.description')}</Typography>
      <Stack direction="column" sx={{mt: 1}}>
        <Typography variant="overline" sx={{color: 'text.secondary', pl: 0.5}}>{t('zoneData.general')}</Typography>
        <Stack direction="row" sx={styles.infoBoxContainer}>
          <InfoBox label={t('zoneData.owners')} value={formatNumber(owners)}/>
          <InfoBox label={t('zoneData.parcels')} value={formatNumber(parcels)}/>
        </Stack>

        <Divider sx={{my: 2}}/>

        <Typography variant="overline" sx={{color: 'text.secondary', pl: 0.5, mb: 1}}>{t('zoneData.concentrate')}</Typography>
        <Stack direction="column" sx={{gap: 1, mb: 2}}>
          <InfoItem label={t('zoneData.parcels_to_concentrate')} icon={<JoinFullIcon sx={styles.infoIcon}/>} value={formatNumber(parcels_to_concentrate)}/>
          <InfoItem valueSuffix={'m2'} label={t('zoneData.area_to_concentrate')} icon={<HighlightAltIcon sx={styles.infoIcon}/>} value={formatNumber(area_to_concentrate)}/>
        </Stack>
        <Stack direction="row" sx={styles.infoBoxContainer}>
          <InfoBox valueSuffix="m²" label={t('zoneData.mean_area_by_owner')} value={formatNumber(mean_area_by_owner)}/>
          <InfoBox valueSuffix="m²" label={t('zoneData.mean_area_by_parcel')} value={formatNumber(mean_area_by_parcel)}/>
        </Stack>
        <Stack direction="row" sx={styles.infoItemContainer}>
          <InfoItem valueSuffix="m²" label={t('zoneData.min_area')} icon={<CloseFullscreenIcon sx={styles.infoIcon}/>} value={formatNumber(min_area)}/>
          <InfoItem valueSuffix="m²" label={t('zoneData.max_area')} icon={<OpenInFullIcon sx={styles.infoIcon}/>} value={formatNumber(max_area)}/>
        </Stack>

        <Divider sx={{my: 2}}/>

        <Typography variant="overline" sx={{color: 'text.secondary', pl: 0.5, mb: 1}}>{t('zoneData.owner')}</Typography>
        <Stack direction="row" sx={styles.infoBoxContainer}>
          <InfoBox label={t('zoneData.parcels_assigned')} value={formatNumber(parcels_assigned)}/>
          <InfoBox label={t('zoneData.mean_parcels_by_owner')} value={formatNumber(mean_parcels_by_owner)}/>
        </Stack>

      </Stack>
    </Stack>
  </Panel>;
};
export default ZoneDataPanel;

const styles = {
  infoBoxContainer: {
    gap: 2,
    mt: 1,
    justifyContent: 'space-between'
  },
  infoItemContainer: {
    flex: '1 1 auto',
    gap: 1,
    mt: 2,
    justifyContent: 'space-around'
  },
  infoIcon: {
    mt: 0.5,
    fontSize: '20px',
    color: 'primary.main'
  }
};